import { getSubjectLabels } from '@/lib/settings';
import React, { useEffect, useMemo, useState } from 'react';
import type { MessageDraft } from '../../lib/trials';
import { addDays, format } from 'date-fns';
import { de } from 'date-fns/locale';
import { fetchTrials, TrialLessonDTO } from '../../lib/trials';
import TeacherTimetable, { type PreviewEvent } from '../schedule/TeacherTimetable';
import WizardShell from '../ui/wizard-shell';
import { cn } from '../../lib/utils';
import CommunicationComposer, { type RecipientGroup } from '../communications/CommunicationComposer';
import CommunicationStandortHint from '../communications/CommunicationStandortHint';
import { getRooms, getRoomsUnits, type Room, type RoomUnit } from '../../lib/rooms';
import { WizardCard, WizardPill, WizardPills, WizardSection, WizardKVGrid, WizardKVItem, WizardYesNoBadge } from '../ui/wizard-layout';
import { historyTemplatesApi } from '../../lib/api';
import { personenApi, type TeacherAvailabilitySlot } from '../../lib/personen';
import { unterrichtseinheitenApi } from '../../lib/unterrichtseinheiten';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import StepDateSelection from './trialWizard/StepDateSelection';
import StepRoomSelection from './trialWizard/StepRoomSelection';
import type { DatePageModel } from './trialWizard/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trial: TrialLessonDTO;
  initialTarget?: { date: string; dayOfWeek: number; startTime: string; endTime: string } | null;
  onConfirm: (payload: {
    date: string;
    startTime: string;
    endTime: string;
    roomId?: number | string | null;
    historyTemplateKey?: string;
    email?: any;
    sms?: { textStudent?: string; textTeacher?: string };
    drafts?: Partial<Record<'student' | 'teacher', MessageDraft>>;
    history?: any;
    channels?: { email?: boolean; sms?: boolean };
    notify?: {
      student?: { email?: boolean; sms?: boolean };
      teacher?: { email?: boolean; sms?: boolean };
    };
  }) => Promise<void>;
}

const timeToMinutes = (value: string): number => {
  const [hRaw, mRaw] = String(value || '').slice(0, 5).split(':');
  const h = Number.parseInt(hRaw || '0', 10);
  const m = Number.parseInt(mRaw || '0', 10);
  return ((Number.isFinite(h) ? h : 0) * 60) + (Number.isFinite(m) ? m : 0);
};

const addMinutesToHHMM = (value: string, minutesToAdd: number): string => {
  const base = timeToMinutes(value);
  const minutesInDay = 24 * 60;
  const normalized = ((base + minutesToAdd) % minutesInDay + minutesInDay) % minutesInDay;
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const toApiDate = (d: Date): string => format(d, 'yyyy-MM-dd');

const addDaysYmd = (ymd: string, days: number): string => {
  const d = new Date(`${ymd}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toApiDate(d);
};

const computeDatePageModel = (
  selectedSlot: { dayOfWeek: number; startTime: string; endTime: string } | null,
  nextAvailableDate: string,
  datePage: number,
  datesPerPage: number,
): DatePageModel => {
  if (!selectedSlot) return null;
  const today = toApiDate(new Date());
  const startDate = addDaysYmd(today, -56);
  const endDate = addDaysYmd(today, 84);

  const all: string[] = [];
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const cand = toApiDate(d);
    const dow = ((d.getDay() + 6) % 7) + 1;
    if (dow === selectedSlot.dayOfWeek) all.push(cand);
  }

  const anchor = nextAvailableDate || today;
  const anchorIdxRaw = all.findIndex((d) => d >= anchor);
  const anchorIdx = Math.max(0, anchorIdxRaw);

  const minPage = -Math.floor(anchorIdx / datesPerPage);
  const maxPage = Math.max(minPage, Math.ceil((all.length - anchorIdx) / datesPerPage) - 1);
  const effectivePage = Math.min(maxPage, Math.max(minPage, datePage));

  const pageIndex = effectivePage - minPage;
  const totalPages = maxPage - minPage + 1;
  const startIdx = anchorIdx + effectivePage * datesPerPage;
  const pageDates = all.slice(Math.max(0, startIdx), Math.max(0, startIdx) + datesPerPage);

  return {
    effectivePage,
    canPrev: effectivePage > minPage,
    canNext: effectivePage < maxPage,
    pageLabel: `Seite ${pageIndex + 1} von ${totalPages}`,
    pageDates: pageDates.map((d) => ({ d, isBeforeAnchor: d < anchor, isPast: d < today })),
  };
};

const computeFilteredRoomsForFach = (rooms: Room[], selectedRoomTags: string[], searchQuery: string): Room[] => {
  const active = rooms.filter((r) => r.aktiv !== false && r.status !== 'inactive');
  const query = searchQuery.trim().toLowerCase();
  const queryFiltered = query
    ? active.filter((room) => {
        const address = String(
          room.address || [room.address_street, room.address_house_number, room.address_postal_code, room.address_city].filter(Boolean).join(' ')
        ).toLowerCase();
        return room.name.toLowerCase().includes(query) || address.includes(query);
      })
    : active;
  if (selectedRoomTags.length === 0) return queryFiltered;
  const selectedNormalized = new Set(selectedRoomTags.map((tag) => tag.trim().toLowerCase()));
  return queryFiltered.filter((room) => {
    const roomTags = new Set((room.tags || []).map((tag) => String(tag).trim().toLowerCase()));
    return Array.from(selectedNormalized).every((selectedTag) => roomTags.has(selectedTag));
  });
};

const computeDefaultRoomTagsForFach = (rooms: Room[], fachNameInput: string): string[] => {
  const fachName = String(fachNameInput || '').trim().toLowerCase();
  if (!fachName) return [];
  const tags = new Set<string>();
  rooms.forEach((room) => {
    (room.tags || []).forEach((tagRaw) => {
      const tag = String(tagRaw || '').trim();
      const normalizedTag = tag.toLowerCase();
      if (!tag) return;
      if (normalizedTag === fachName || normalizedTag.includes(fachName) || fachName.includes(normalizedTag)) {
        tags.add(tag);
      }
    });
  });
  return Array.from(tags);
};

const RescheduleTrialModal: React.FC<Props> = ({ open, onOpenChange, trial, initialTarget = null, onConfirm }) => {
  const [step, setStep] = useState<number>(1);
  // Step 1 (Stundenplan): stabiler Referenztag + Wochentagswechsel (mobile)
  const [timetableBaseDate, setTimetableBaseDate] = useState<Date>(new Date());
  const [timetableDayOfWeek, setTimetableDayOfWeek] = useState<number>(() => {
    const jsDow = new Date().getDay();
    return jsDow === 0 ? 7 : jsDow; // 1..7 (Mo=1)
  });

  const [selectedSlot, setSelectedSlot] = useState<{ dayOfWeek: number; startTime: string; endTime: string } | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(trial.date);
  const [datePage, setDatePage] = useState<number>(0);
  const [nextAvailableDate, setNextAvailableDate] = useState<string>('');
  const [nextAvailableLoading, setNextAvailableLoading] = useState<boolean>(false);

  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomUnitsForDate, setRoomUnitsForDate] = useState<RoomUnit[]>([]);
  const [roomUnitsLoading, setRoomUnitsLoading] = useState<boolean>(false);
  const [roomUnitsError, setRoomUnitsError] = useState<string>('');
  const [selectedRoomTags, setSelectedRoomTags] = useState<string[]>([]);
  const [roomTagsUserModified, setRoomTagsUserModified] = useState<boolean>(false);
  const [roomSearchQuery, setRoomSearchQuery] = useState<string>('');
  const [roomPreviewOpen, setRoomPreviewOpen] = useState<boolean>(false);
  const [roomPreview, setRoomPreview] = useState<Room | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<number | undefined>(() => {
    const rid = (trial.roomId as any) ?? null;
    const n = typeof rid === 'string' ? Number.parseInt(rid, 10) : (typeof rid === 'number' ? rid : undefined);
    return Number.isFinite(n as any) ? (n as number) : undefined;
  });

  const [emailSubjectStudent, setEmailSubjectStudent] = useState<string>('Ihre Probestunde wurde verschoben');
  const [emailHtmlStudent, setEmailHtmlStudent] = useState<string>(`<p>Hallo ${trial.studentName},</p><p>Ihre Probestunde wurde verschoben.</p>`);
  const [emailSubjectTeacher, setEmailSubjectTeacher] = useState<string>('Probestunde verschoben');
  const [emailHtmlTeacher, setEmailHtmlTeacher] = useState<string>(`<p>Hallo ${trial.teacherName || 'Lehrer'},</p><p>Eine Probestunde wurde verschoben.</p>`);
  const [sendEmailStudent, setSendEmailStudent] = useState<boolean>(true);
  const [sendEmailTeacher, setSendEmailTeacher] = useState<boolean>(true);
  const [sendSmsStudent, setSendSmsStudent] = useState<boolean>(false);
  const [sendSmsTeacher, setSendSmsTeacher] = useState<boolean>(false);
  const [smsTextStudent, setSmsTextStudent] = useState<string>('');
  const [smsTextTeacher, setSmsTextTeacher] = useState<string>('');
  const [historyText, setHistoryText] = useState<string>('');
  // Sobald jemand den Text selbst anfasst, bleibt er stehen; vorher zieht er mit dem
  // gewählten Termin mit. Ohne diese Unterscheidung fror der Eintrag beim Öffnen ein
  // und nannte am Ende den alten Termin statt des neuen.
  const [historyTouched, setHistoryTouched] = useState(false);
  const [teacherAvailability, setTeacherAvailability] = useState<TeacherAvailabilitySlot[]>([]);
  const [teacherAvailabilityLoading, setTeacherAvailabilityLoading] = useState<boolean>(false);
  const trialDurationMinutes = useMemo(() => {
    const start = timeToMinutes(trial.startTime || '00:00');
    const end = timeToMinutes(trial.endTime || '00:00');
    let diff = end - start;
    if (diff <= 0) diff += 24 * 60;
    return diff > 0 ? diff : 30;
  }, [trial.startTime, trial.endTime]);

  useEffect(() => {
    let cancelled = false;
    const loadHistoryTemplate = async () => {
      if (!open || historyTouched) return;
      try {
        const roomName = rooms.find((r) => r.id === selectedRoomId)?.name || trial.roomName || '';
        const rendered = await historyTemplatesApi.render({
          template_key: 'trial.rescheduled.history',
          standort_id: standortId || undefined,
          vars: {
            studentName: trial.studentName || '',
            date: selectedDate,
            startTime: selectedSlot?.startTime.slice(0, 5) || trial.startTime,
            endTime: selectedSlot?.endTime.slice(0, 5) || trial.endTime,
            roomName,
            roomClause: roomName ? ` im Raum ${roomName}` : '',
          },
        });
        if (cancelled) return;
        if (rendered.history) setHistoryText(rendered.history);
      } catch {
        // fallback uses computeDefaultHistory
      }
    };
    void loadHistoryTemplate();
    return () => {
      cancelled = true;
    };
  }, [open, historyTouched, rooms, selectedRoomId, trial.roomName, trial.studentName, trial.startTime, trial.endTime, selectedDate, selectedSlot]);

  const slotWithTrialDuration = (dayOfWeek: number, startTime: string) => {
    const startHHMM = String(startTime || '').slice(0, 5);
    const endHHMM = addMinutesToHHMM(startHHMM, trialDurationMinutes);
    return {
      dayOfWeek,
      startTime: `${startHHMM}:00`,
      endTime: `${endHHMM}:00`,
    };
  };

  const standortId = useMemo(() => {
    const raw = (trial as any).standort_id ?? (trial as any).standortId ?? null;
    const n = typeof raw === 'string' ? Number.parseInt(raw, 10) : raw;
    return (typeof n === 'number' && Number.isFinite(n) && n > 0) ? n : undefined;
  }, [trial]);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setTimetableBaseDate(new Date());
    setTimetableDayOfWeek(() => {
      const jsDow = new Date().getDay();
      return jsDow === 0 ? 7 : jsDow;
    });
    if (initialTarget) {
      const startHHMM = String(initialTarget.startTime || '').slice(0, 5);
      const endHHMM = addMinutesToHHMM(startHHMM, trialDurationMinutes);
      setSelectedSlot({
        dayOfWeek: initialTarget.dayOfWeek,
        startTime: `${startHHMM}:00`,
        endTime: `${endHHMM}:00`,
      });
      setSelectedDate(initialTarget.date);
    } else {
      setSelectedSlot(null);
      setSelectedDate(trial.date);
    }
    setHistoryText('');
    setHistoryTouched(false);
    setDatePage(0);
    setNextAvailableDate('');
    setSelectedRoomTags([]);
    setRoomTagsUserModified(false);
    setRoomSearchQuery('');
    setSendEmailStudent(true);
    setSendEmailTeacher(true);
    setSendSmsStudent(false);
    setSendSmsTeacher(false);
  }, [open, trial.date, initialTarget, trialDurationMinutes]);

  // Räume laden (Standort)
  useEffect(() => {
    let cancelled = false;
    const loadRooms = async () => {
      if (!open) return;
      if (!standortId) { setRooms([]); return; }
      try {
        const list = await getRooms(standortId);
        if (cancelled) return;
        // Nur aktive Räume
        const active = (list || []).filter(r => r.aktiv !== false && r.status !== 'inactive');
        setRooms(active);
        // default: vorhandener Raum bleibt, sonst erster aktiver Raum
        if (!selectedRoomId && active.length > 0) {
          setSelectedRoomId(active[0].id);
        }
      } catch {
        if (!cancelled) setRooms([]);
      }
    };
    loadRooms();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, standortId]);

  const teacherIdNum = useMemo(() => {
    const t = Number(trial.teacherId || 0);
    return Number.isFinite(t) && t > 0 ? t : undefined;
  }, [trial.teacherId]);

  useEffect(() => {
    const loadAvailability = async () => {
      if (!open || !teacherIdNum || !standortId) {
        setTeacherAvailability([]);
        return;
      }
      setTeacherAvailabilityLoading(true);
      try {
        const slots = await personenApi.getTeacherAvailability(teacherIdNum, standortId);
        setTeacherAvailability(slots || []);
      } catch {
        setTeacherAvailability([]);
      } finally {
        setTeacherAvailabilityLoading(false);
      }
    };
    void loadAvailability();
  }, [open, teacherIdNum, standortId]);

  // Raumbelegungen für Datum laden (damit wir frei/belegt anzeigen können)
  useEffect(() => {
    let cancelled = false;
    const loadUnits = async () => {
      if (!open) return;
      if (!selectedDate) { setRoomUnitsForDate([]); return; }
      const ids = (rooms || []).map(r => r.id);
      if (ids.length === 0) { setRoomUnitsForDate([]); return; }
      setRoomUnitsLoading(true);
      setRoomUnitsError('');
      try {
        const units = await getRoomsUnits(ids, selectedDate, selectedDate);
        if (cancelled) return;
        setRoomUnitsForDate(units || []);
      } catch {
        if (!cancelled) setRoomUnitsForDate([]);
        if (!cancelled) setRoomUnitsError('Raumbelegung konnte nicht geladen werden.');
      } finally {
        if (!cancelled) setRoomUnitsLoading(false);
      }
    };
    loadUnits();
    return () => { cancelled = true; };
  }, [open, selectedDate, rooms]);

  useEffect(() => {
    if (!open || roomTagsUserModified) return;
    setSelectedRoomTags(computeDefaultRoomTagsForFach(rooms, String((trial as any).fachName || '')));
  }, [open, roomTagsUserModified, rooms, trial]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!open || !selectedSlot || !teacherIdNum) {
        setNextAvailableDate('');
        return;
      }
      setNextAvailableLoading(true);
      try {
        const todayYmd = toApiDate(new Date());
        const endDate = addDaysYmd(todayYmd, 56);
        const candidates: string[] = [];
        const start = new Date(`${addDaysYmd(todayYmd, -28)}T00:00:00`);
        const end = new Date(`${endDate}T00:00:00`);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const cand = toApiDate(d);
          const dow = ((d.getDay() + 6) % 7) + 1;
          if (dow === selectedSlot.dayOfWeek) candidates.push(cand);
        }

        const [units, trials] = await Promise.all([
          unterrichtseinheitenApi.list({ teacherId: teacherIdNum, start: todayYmd, end: endDate }),
          fetchTrials(todayYmd, endDate),
        ]);

        const busyByDate = new Map<string, Array<{ s: string; e: string }>>();
        (units || []).forEach((u) => {
          const ymd = u.start_zeit.slice(0, 10);
          const arr = busyByDate.get(ymd) || [];
          arr.push({ s: u.start_zeit.slice(11, 16), e: u.end_zeit.slice(11, 16) });
          busyByDate.set(ymd, arr);
        });
        (trials || [])
          .filter((t) => String(t.teacherId || '') === String(teacherIdNum))
          .forEach((t) => {
            const arr = busyByDate.get(t.date) || [];
            arr.push({ s: t.startTime.slice(0, 5), e: t.endTime.slice(0, 5) });
            busyByDate.set(t.date, arr);
          });

        const selStart = selectedSlot.startTime.slice(0, 5);
        const selEnd = selectedSlot.endTime.slice(0, 5) || addMinutesToHHMM(selStart, trialDurationMinutes);
        const firstFree =
          candidates.find((d) => {
            if (d < todayYmd) return false;
            const busy = busyByDate.get(d) || [];
            return !busy.some((b) => overlaps(selStart, selEnd, b.s, b.e));
          }) || candidates.find((d) => d >= todayYmd) || '';
        if (!cancelled) setNextAvailableDate(firstFree);
      } catch {
        if (!cancelled) setNextAvailableDate(selectedDate || '');
      } finally {
        if (!cancelled) setNextAvailableLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [open, selectedSlot?.dayOfWeek, selectedSlot?.startTime, selectedSlot?.endTime, teacherIdNum, trialDurationMinutes, selectedDate]);

  const overlaps = (startA: string, endA: string, startB: string, endB: string): boolean => {
    return startA < endB && startB < endA;
  };

  const timetableReferenceDate = useMemo(() => {
    const base = timetableBaseDate;
    const jsDow = base.getDay(); // 0..6 (So..Sa)
    const baseDow = jsDow === 0 ? 7 : jsDow; // 1..7 (Mo..So)
    const diff = Number(timetableDayOfWeek || baseDow) - baseDow;
    const d = new Date(base);
    d.setDate(base.getDate() + diff);
    return d;
  }, [timetableBaseDate, timetableDayOfWeek]);

  // PreviewEvent für ausgewählten Slot erstellen
  const previewEvent: PreviewEvent | null = useMemo(() => {
    if (!selectedSlot || !selectedDate) return null;
    return {
      dayOfWeek: selectedSlot.dayOfWeek,
      startTime: selectedSlot.startTime.slice(0, 5),
      endTime: selectedSlot.endTime.slice(0, 5),
      title: 'Probestunde verschoben',
      subtitle: trial.studentName,
      fachName: (trial as any).fachName || 'Probestunde',
      color: 'bg-amber-100 border-amber-300 text-amber-800',
    };
  }, [selectedSlot, selectedDate, trial]);

  const selectedSlotOutsideAvailability = useMemo(() => {
    if (!selectedSlot) return false;
    if (teacherAvailability.length === 0) return false;
    const hhmm = selectedSlot.startTime.slice(0, 5);
    const daySlots = teacherAvailability.filter((s) => Number(s.dayOfWeek) === Number(selectedSlot.dayOfWeek));
    if (daySlots.length === 0) return true;
    const tMin = timeToMinutes(hhmm);
    return !daySlots.some((s) => tMin >= timeToMinutes(s.time_start) && tMin < timeToMinutes(s.time_end));
  }, [selectedSlot, teacherAvailability]);

  const datePageModel = useMemo(
    () => computeDatePageModel(selectedSlot, nextAvailableDate || selectedDate, datePage, 4),
    [selectedSlot, nextAvailableDate, selectedDate, datePage]
  );

  const allRoomTags = useMemo(() => {
    const tagSet = new Set<string>();
    rooms
      .filter((room) => room.aktiv !== false && room.status !== 'inactive')
      .forEach((room) => {
        (room.tags || []).forEach((tagRaw) => {
          const tag = String(tagRaw || '').trim();
          if (tag) tagSet.add(tag);
        });
      });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b, 'de'));
  }, [rooms]);

  const filteredRoomsForFach = useMemo(
    () => computeFilteredRoomsForFach(rooms, selectedRoomTags, roomSearchQuery),
    [rooms, selectedRoomTags, roomSearchQuery]
  );

  const computeDefaultHistory = (newDate: string, newStart: string, newEnd: string) => {
    return `Probestunde verschoben: ${trial.date} ${trial.startTime}–${trial.endTime} → ${newDate} ${newStart}–${newEnd}`;
  };

  // Composer groups for student + teacher notification
  const composerGroups: RecipientGroup[] = useMemo(() => {
    const sp = String(trial.subprofileId || '').trim();
    let wpUserId = 0;
    let subIndex: number | null = null;
    if (sp && sp.includes('-')) {
      const [a, b] = sp.split('-', 2);
      wpUserId = Number.parseInt(a, 10) || 0;
      subIndex = b ? (Number.parseInt(b, 10) || null) : null;
    } else if (sp) {
      wpUserId = Number.parseInt(sp, 10) || 0;
    }
    if (!wpUserId) return [];
    const roomName = rooms.find((r) => Number(r.id) === Number(selectedRoomId))?.name || '';
    const sharedVars = {
      studentName: trial.studentName || '',
      teacherName: trial.teacherName || '',
      date: selectedDate,
      startTime: (selectedSlot?.startTime || trial.startTime || '').slice(0, 5),
      endTime: (selectedSlot?.endTime || trial.endTime || '').slice(0, 5),
      roomName,
      roomClause: roomName ? ` im Raum ${roomName}` : '',
    };

    const groups: RecipientGroup[] = [];
    if (teacherIdNum) {
      groups.push({
        key: 'teacher',
        label: 'Lehrer',
        recipients: [{ id: `person:${teacherIdNum}:`, type: 'person', wpUserId: teacherIdNum, subIndex: null, name: trial.teacherName || `Lehrer #${teacherIdNum}` }],
        templateKeys: { email: 'trial.rescheduled.teacher', sms: 'trial.rescheduled.teacher' },
        vars: sharedVars,
        defaultSelectedChannels: { email: true, sms: false },
      });
    }
    if (wpUserId) {
      groups.push({
        key: 'student',
        label: 'Teilnehmer',
        recipients: [{ id: `person:${wpUserId}:${subIndex ?? ''}`, type: 'person', wpUserId, subIndex, name: trial.studentName || 'Teilnehmer' }],
        templateKeys: { email: 'trial.rescheduled.student', sms: 'trial.rescheduled.student' },
        vars: sharedVars,
        defaultSelectedChannels: { email: true, sms: false },
      });
    }
    return groups;
  }, [trial, selectedDate, selectedSlot, selectedRoomId, rooms, teacherIdNum]);

  const handleConfirm = async () => {
    if (!selectedSlot) return;
    if (!selectedDate) return;
    const newDate = selectedDate;
    const newStart = selectedSlot.startTime.slice(0,5);
    const newEnd = selectedSlot.endTime.slice(0,5);
    const subId = trial.subprofileId as unknown;
    let wpUserId: number | undefined = undefined;
    let subIndex: number | undefined = undefined;
    if (typeof subId === 'string' && subId.includes('-')) {
      const parts = subId.split('-');
      wpUserId = Number.parseInt(parts[0], 10);
      if (parts[1]) subIndex = Number.parseInt(parts[1], 10);
    }
    const hist = historyText || computeDefaultHistory(newDate, newStart, newEnd);
    await onConfirm({
      date: newDate,
      startTime: newStart,
      endTime: newEnd,
      roomId: selectedRoomId ?? null,
      historyTemplateKey: 'trial.rescheduled.history',
      email: {
        subjectStudent: emailSubjectStudent,
        htmlStudent: emailHtmlStudent,
        subjectTeacher: emailSubjectTeacher,
        htmlTeacher: emailHtmlTeacher,
      },
      sms: { textStudent: smsTextStudent, textTeacher: smsTextTeacher },
      // Der bearbeitete Text je Empfänger, wie ihn das Backend liest
      // (trial_mail.entwurf_aus). Bisher gab es dafür keinen Empfänger (T-0065).
      drafts: {
        student: { subject: emailSubjectStudent, html: emailHtmlStudent, sms: smsTextStudent },
        teacher: { subject: emailSubjectTeacher, html: emailHtmlTeacher, sms: smsTextTeacher },
      },
      history: {
        wpUserId,
        subIndex,
        description: hist,
        type: 'trial.rescheduled',
      },
      channels: {
        email: sendEmailStudent || sendEmailTeacher,
        sms: sendSmsStudent || sendSmsTeacher,
      },
      notify: {
        student: { email: sendEmailStudent, sms: sendSmsStudent },
        teacher: { email: sendEmailTeacher, sms: sendSmsTeacher },
      },
    });
  };

  // Schritt 1: Zeit im Stundenplan wählen
  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="rounded-md border bg-muted/20 p-3">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <div className="text-xs text-muted-foreground">{getSubjectLabels().singular}</div>
            <div className="text-sm font-medium">{(trial as any).fachName || trial.instrument || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Lehrer</div>
            <div className="text-sm font-medium">{trial.teacherName || '—'}</div>
          </div>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        Uhrzeit im Stundenplan wählen, Datum im nächsten Schritt festlegen.
        {' '}
        {teacherAvailabilityLoading
          ? 'Freigabezeiten werden geladen…'
          : teacherAvailability.length > 0
            ? 'Freigabezeiten sind im Stundenplan farblich markiert.'
            : 'Keine Freigabezeiten gepflegt – es gelten nur Belegungen/Kollisionen.'}
      </div>
      <div className="md:hidden">
        <div className="flex flex-wrap gap-1">
          {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((lbl, idx) => {
            const dow = idx + 1;
            const active = Number(timetableDayOfWeek) === dow;
            return (
              <button
                key={lbl}
                type="button"
                className={cn(
                  'px-2 py-1 rounded-md border text-xs',
                  active ? 'border-primary bg-primary/5 text-foreground' : 'border-muted text-muted-foreground hover:bg-muted/50'
                )}
                onClick={() => setTimetableDayOfWeek(dow)}
              >
                {lbl}
              </button>
            );
          })}
        </div>
        <div className="mt-1 text-[11px] text-muted-foreground">
          In der mobilen Ansicht wechseln Sie hier den Wochentag (kein Datum).
        </div>
      </div>

      <div className="rounded-lg border">
        <TeacherTimetable
          teacherId={trial.teacherId || ''}
          referenceDate={timetableReferenceDate}
          selectable={true}
          displayMode="auto"
          showNavigation={false}
          allowOccupiedSelection={true}
          availabilitySlots={teacherAvailability}
          selectedSlot={
            selectedSlot
              ? { dayOfWeek: selectedSlot.dayOfWeek, startTime: selectedSlot.startTime.slice(0, 5) }
              : null
          }
          previewEvent={previewEvent}
          onSelectSlot={({ dayOfWeek, startTime }) => {
            setSelectedSlot(slotWithTrialDuration(dayOfWeek, startTime));
            setTimetableDayOfWeek(dayOfWeek);
            // Default: nächster Termin dieses Wochentags
            const d = new Date();
            for (let i = 0; i < 28; i++) {
              const cand = addDays(d, i);
              const candDow = ((cand.getDay() + 6) % 7) + 1;
              if (candDow === dayOfWeek) {
                setSelectedDate(format(cand, 'yyyy-MM-dd'));
                break;
              }
            }
          }}
        />
      </div>
      {selectedSlotOutsideAvailability ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900">
          Hinweis: Der ausgewählte Slot liegt außerhalb der Freigabezeiten des Lehrers.
        </div>
      ) : null}
    </div>
  );

  // Schritt 2: Datum wählen
  const renderStep2 = () => (
    <StepDateSelection
      selectedSlot={selectedSlot}
      step2HintText={nextAvailableLoading ? 'Ermittle nächstmöglichen Termin…' : (nextAvailableDate ? `Vorschlag: ${format(new Date(`${nextAvailableDate}T00:00:00`), 'dd.MM.yyyy')}` : '')}
      datePageModel={datePageModel}
      date={selectedDate}
      startTime={(selectedSlot?.startTime || '').slice(0, 5)}
      teacherDayLoading={false}
      teacherDayError=""
      hasTeacherCollision={false}
      showSelectedSummary={false}
      onPrevPage={() => setDatePage((prev) => prev - 1)}
      onNextPage={() => setDatePage((prev) => prev + 1)}
      onSelectDate={(nextDate) => setSelectedDate(nextDate)}
      addMinutes={(hhmm, mins) => addMinutesToHHMM(hhmm, mins || trialDurationMinutes)}
    />
  );

  // Schritt 3: Raum wählen
  const renderStep3 = () => {
    const fachId = Number((trial as any).fachId || 0) || undefined;
    const fachName = String((trial as any).fachName || trial.instrument || '').trim();
    const fachOptions = fachId ? [{ id: fachId, name: fachName || getSubjectLabels().singular }] : [];
    return (
      <>
        <StepRoomSelection
          date={selectedDate}
          startTime={selectedSlot?.startTime.slice(0, 5) || trial.startTime.slice(0, 5)}
          endTime={selectedSlot?.endTime.slice(0, 5) || trial.endTime.slice(0, 5)}
          roomUnitsLoading={roomUnitsLoading}
          roomUnitsError={roomUnitsError}
          filteredRoomsForFach={filteredRoomsForFach}
          roomUnitsForDate={roomUnitsForDate}
          roomId={selectedRoomId}
          teacherRoomsOnSelectedDay={new Set<number>()}
          fachId={fachId}
          fachOptions={fachOptions}
          allRoomTags={allRoomTags}
          selectedRoomTags={selectedRoomTags}
          roomSearchQuery={roomSearchQuery}
          onToggleRoomTag={(tag) => {
            setRoomTagsUserModified(true);
            setSelectedRoomTags((prev) => (prev.includes(tag) ? prev.filter((value) => value !== tag) : [...prev, tag]));
          }}
          onChangeRoomSearchQuery={setRoomSearchQuery}
          onSelectRoom={setSelectedRoomId}
          onOpenRoomPreview={(room) => {
            setRoomPreview(room);
            setRoomPreviewOpen(true);
          }}
          addMinutes={(hhmm, mins) => addMinutesToHHMM(hhmm, mins)}
          overlaps={overlaps}
        />
        <Dialog open={roomPreviewOpen} onOpenChange={setRoomPreviewOpen}>
          <DialogContent size="2xl">
            <DialogHeader>
              <DialogTitle>{roomPreview?.name || 'Raum'}</DialogTitle>
            </DialogHeader>
            {roomPreview?.image_url ? (
              <div className="space-y-3">
                <img src={roomPreview.image_url} alt={roomPreview.name} className="w-full rounded-lg border object-contain max-h-[60vh]" />
                {roomPreview.address ? <div className="text-sm text-muted-foreground">{roomPreview.address}</div> : null}
              </div>
            ) : (
              <div className="space-y-2">
                {roomPreview?.address ? <div className="text-sm text-muted-foreground">{roomPreview.address}</div> : null}
                {roomPreview?.tags && roomPreview.tags.length > 0 ? (
                  <div className="pt-2">
                    <div className="text-sm font-medium mb-2">Ausstattung</div>
                    <WizardPills>
                      {roomPreview.tags.map((t) => (
                        <WizardPill key={t} active={false}>{t}</WizardPill>
                      ))}
                    </WizardPills>
                  </div>
                ) : null}
                <div className="text-sm text-muted-foreground">Keine weiteren Details vorhanden.</div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  };

  // Schritt 4: Kommunikation
  const renderStep4 = () => (
    <div className="space-y-4">
      <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
        E-Mail und SMS können je Empfänger separat aktiviert oder deaktiviert werden.
      </div>
      {composerGroups.length > 0 && standortId > 0 && (
        <CommunicationComposer
          active={open && step === 4}
          standortId={standortId}
          groups={composerGroups}
          onDraftsChange={({ groups: g }) => {
            const s = g['student'];
            const t = g['teacher'];
            if (s) {
              setSendEmailStudent(!!s.selectedChannels.email);
              setSendSmsStudent(!!s.selectedChannels.sms);
              setEmailSubjectStudent(s.email.subjectTpl || '');
              setEmailHtmlStudent(s.email.htmlTpl || '');
              setSmsTextStudent(s.sms.smsTpl || '');
            }
            if (t) {
              setSendEmailTeacher(!!t.selectedChannels.email);
              setSendSmsTeacher(!!t.selectedChannels.sms);
              setEmailSubjectTeacher(t.email.subjectTpl || '');
              setEmailHtmlTeacher(t.email.htmlTpl || '');
              setSmsTextTeacher(t.sms.smsTpl || '');
            }
          }}
        />
      )}
      {composerGroups.length > 0 && standortId <= 0 ? (
        <CommunicationStandortHint />
      ) : null}
    </div>
  );

  // Schritt 5: Zusammenfassung (inkl. Historie)
  const renderStep5 = () => {
    const newStart = (selectedSlot?.startTime || '').slice(0, 5);
    const newEnd = (selectedSlot?.endTime || '').slice(0, 5);
    const roomName = rooms.find((r) => Number(r.id) === Number(selectedRoomId))?.name || '—';
    return (
      <div className="space-y-4">
        <WizardCard>
          <WizardSection title="Unterricht">
            <WizardKVGrid>
              <WizardKVItem label={getSubjectLabels().singular}>{(trial as any).fachName || trial.instrument || '—'}</WizardKVItem>
              <WizardKVItem label="Lehrer">{trial.teacherName || '—'}</WizardKVItem>
              <WizardKVItem label="Datum">{selectedDate ? format(new Date(`${selectedDate}T00:00:00`), 'EEEE, dd.MM.yyyy', { locale: de }) : '—'}</WizardKVItem>
              <WizardKVItem label="Zeit">{newStart}–{newEnd}</WizardKVItem>
            </WizardKVGrid>
          </WizardSection>
          <WizardSection title="Ort">
            <WizardKVGrid>
              <WizardKVItem label="Raum">{roomName}</WizardKVItem>
            </WizardKVGrid>
          </WizardSection>
          <WizardSection title="Benachrichtigungen">
            <WizardKVGrid>
              <WizardKVItem label="E-Mail an Lehrer"><WizardYesNoBadge yes={sendEmailTeacher} /></WizardKVItem>
              <WizardKVItem label="E-Mail an Teilnehmer"><WizardYesNoBadge yes={sendEmailStudent} /></WizardKVItem>
              <WizardKVItem label="SMS an Lehrer"><WizardYesNoBadge yes={sendSmsTeacher} /></WizardKVItem>
              <WizardKVItem label="SMS an Teilnehmer"><WizardYesNoBadge yes={sendSmsStudent} /></WizardKVItem>
            </WizardKVGrid>
          </WizardSection>
          <WizardSection title="Event-Historie">
            <div className="grid gap-2">
              <label className="text-sm font-medium" htmlFor="reschedule-history">Historieneintrag</label>
              <Textarea
                id="reschedule-history"
                className="min-h-[120px] w-full rounded-md border p-2 text-sm"
                value={historyText}
                onChange={e => {
                  setHistoryTouched(true);
                  setHistoryText(e.target.value);
                }}
                placeholder={computeDefaultHistory(
                  selectedDate,
                  selectedSlot?.startTime.slice(0, 5) || trial.startTime,
                  selectedSlot?.endTime.slice(0, 5) || trial.endTime
                )}
              />
              <div className="text-xs text-muted-foreground">
                Dieser Text wird beim Bestätigen als Historieneintrag gespeichert und kann bei Bedarf angepasst werden.
              </div>
            </div>
          </WizardSection>
        </WizardCard>
      </div>
    );
  };

  const canNext = useMemo(() => {
    if (step === 1) return !!selectedSlot && !!selectedDate;
    if (step === 2) return !!selectedSlot && !!selectedDate;
    if (step === 3) return !!selectedRoomId;
    // Kommunikation + Übersicht sind optional (Weiter/Bestätigen steuern trotzdem)
    if (step === 4) return true;
    return false;
  }, [step, selectedSlot, selectedDate, selectedRoomId]);

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Probestunde verschieben"
      description={`Schritt ${step} von 5`}
      currentStep={step}
      steps={[
        { label: 'Datum & Zeit' },
        { label: 'Datum' },
        { label: 'Raum' },
        { label: 'Kommunikation' },
        { label: 'Übersicht' },
      ]}
      onBack={step > 1 ? () => setStep(step - 1) : undefined}
      canBack={step > 1}
      onNext={step < 5 ? () => {
        setStep(step + 1);
      } : undefined}
      canNext={step < 5 ? canNext : false}
      nextLabel="Weiter"
      onPrimary={step === 5 ? handleConfirm : undefined}
      primaryLabel="Bestätigen"
      canPrimary={step === 5 ? (!!selectedSlot && !!selectedDate && !!selectedRoomId) : false}
    >
      <div className="py-4">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
      </div>
    </WizardShell>
  );
};

export default RescheduleTrialModal;
